// Central feature flags.
//
// Flip a value here to hide or show a whole feature app-wide without deleting any code, so a
// feature parked for a business reason can be reintroduced with a one-line change. Nothing is
// removed from the codebase or the database while a feature is hidden — the components, API
// methods, backend endpoints and any existing data all stay intact and dormant.
export const FEATURES = {
  // Patient Tags — parked 2026-06-01 by business decision. While false, the following are
  // hidden: the Tags picker on the patient registration/edit form, the tag pills shown in the
  // patient list and detail views, the Settings → Tags admin page (sidebar submenu), and the
  // "Settings — Tags" row in Roles & Permissions / Edit User Permissions. Set to true to bring
  // the entire feature back; no other changes are required. Existing patient tag data is
  // preserved in the database the whole time.
  patientTags: false,

  // Clinical Notes tab in the Patient Details modal — parked 2026-08-11 by business decision.
  // While false the tab is hidden from the modal and its "Clinical Notes tab" toggle is hidden
  // from Roles & Permissions / Edit User Permissions. The notes themselves are untouched: they
  // stay on every prescription and still print, so flipping this back to true restores the tab
  // with all its history intact.
  patientClinicalNotesTab: false,

  // Receivables report — parked 2026-09-07 with the FINALIZED invoice state it was built on.
  // The query is `status = 'FINALIZED' AND amount_pending > 0`, and an invoice now goes straight
  // from DRAFT to PAID, so nothing can ever land in it again and the report would sit permanently
  // empty. The endpoint, query and component are all untouched: if a bill-now-pay-later flow ever
  // comes back, this flips to true and the report works again.
  receivablesReport: false,

  // Punching from a desktop, parked 2026-10-03 by business decision: every punch is to come from
  // the person's own phone. A desktop has no camera worth the name and places itself by its
  // network address, so the selfie and the position a desktop punch records are both close to
  // worthless. While this is false, My Attendance offers only "Punch in with your phone" on a
  // desktop, which hands the punch to the phone by a scanned code. On a phone-sized screen the
  // button stays, because that already IS the phone, with the camera and the GPS. Nothing is
  // removed: PunchDialog, the endpoints and every existing entry are untouched.
  punchFromDesktop: false,

  // Leave ALLOWANCES — the yearly days each kind of leave is worth — parked 2026-10-06 by the
  // owner: "I will decide the numbers and then we can introduce again". Annual 14, Casual 10 and
  // Sick 8 are the seeded defaults, untouched since the day they were created, and no leave has
  // ever been requested against them; telling staff they have 14 days when nobody has decided that
  // is worse than telling them nothing.
  //
  // While this is false, the two places a member of staff is shown a figure are hidden: the balance
  // chips on My Attendance, and the "left after this" line in the Request Leave dialog. Requesting
  // leave still works, the types are still offered, and the Leave Balances report still shows an
  // administrator the whole picture, because an administrator is who decides the numbers.
  //
  // Nothing is removed: the quotas, the API and the components are untouched, so setting the real
  // figures under Attendance then Leave Types and flipping this to true brings them straight back.
  leaveAllowances: false,

  // Share on WhatsApp for purchase orders — parked 2026-10-28 by the owner: "Remove this Whatsapp
  // functionality for now and just add download functionality". While false, the Share on WhatsApp
  // button on an accepted order review and the WhatsApp icon on the Orders list are hidden, and
  // Download PDF stands in for both. Nothing is removed: orderShare.js (the typed-out message, the
  // supplier's number, the chat link) and its tests are untouched, so flipping this to true brings
  // the buttons straight back.
  whatsappShare: false,
};

// Permission modules to hide from the Roles & Permissions and per-user override screens while a
// feature is parked. Derived from FEATURES so the flag above is the single switch.
export const HIDDEN_PERMISSION_MODULES = [
  ...(FEATURES.patientTags ? [] : ['SETTINGS_TAGS']),
];

// Sidebar menu/submenu ids to hide while a feature is parked. Derived from FEATURES.
export const HIDDEN_MENU_IDS = [
  ...(FEATURES.patientTags ? [] : ['tags']),
];

// Individual permission ACTIONS to hide from the permissions screens while a feature is parked.
// Modules are hidden wholesale via HIDDEN_PERMISSION_MODULES above; this is the finer-grained
// equivalent for a single toggle whose feature is off. Applied inside each screen's actionsFor()
// helper, so every call site (rendering and the grant/deny-all toggles) honours it.
export const HIDDEN_PERMISSION_ACTIONS = [
  ...(FEATURES.patientClinicalNotesTab ? [] : ['VIEW_CLINICAL_NOTES_TAB']),
];
